import { compatibility, useGetUrl } from '@seek/ca-myprofile-utils';
import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Heading, Stack, Text, TextLink } from 'braid-design-system';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useIsPublicProfileEnabled } from 'src/modules/features/useIsPublicProfileEnabled';
import { useTypedSelector } from 'src/reducers';
import getBrandSpecificDescription from 'src/utils/getBrandSpecificDescription';
import { useGetPageTitle } from 'src/utils/useGetPageTitle';

import { LoadingItem } from '../LoadingItem/LoadingItem';
import { useUserProfile } from '../Notifications/hooks/useUserProfile';
import { PageTitles } from '../Notifications/pageTitle';

import translations from './.vocab';
import { ProfileVisibility } from './ProfileVisibility/ProfileVisibility';

export const Visibility = () => {
  const site = useTypedSelector((state) => state.page.pageAnalytics.site);
  const brand = useTypedSelector((state) => state.page.pageAnalytics.brand);
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const { language: melwaysLanguage } = useMelwaysInfo();
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(melwaysLanguage);

  const { t } = useTranslations(translations);
  const { user } = useUserProfile();
  const { trackPageLoad } = useAnalytics();

  const isPublicProfileEnabled = useIsPublicProfileEnabled();

  useEffect(() => {
    if (user) {
      trackPageLoad({ currentPage: 'visibility settings' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Disabling dependency on trackPageLoad to only track initial tab render

  const getUrl = useGetUrl(zone, language);
  const privacyUrl = getUrl('profile-privacy');

  const pageTitle = useGetPageTitle(site, language, 'Visibility Settings');

  const sectionHeading = isPublicProfileEnabled
    ? 'Where can your profile be seen?'
    : t('Where can my profile be seen?');

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={getBrandSpecificDescription(site, language)}
        />
      </Helmet>
      <Stack space="gutter">
        {!user ? (
          <LoadingItem size="large" width="medium" />
        ) : (
          <Heading level="4">{sectionHeading}</Heading>
        )}
        <Stack space="small">
          <ProfileVisibility />
        </Stack>
        {user ? (
          <Stack space="medium">
            <Text weight="medium">
              {t('Applications on BRAND', { brand: PageTitles[brand] })}
            </Text>
            <Text>
              {t('For all settings, your Profile including...', {
                TextLink: (children: React.ReactNode[]) => (
                  <TextLink
                    key="privacyPolicyPath"
                    target="_blank"
                    href={privacyUrl}
                    hitArea="standard"
                    weight="weak"
                  >
                    {children}
                  </TextLink>
                ),
              })}
            </Text>
          </Stack>
        ) : (
          <Stack space="medium">
            <LoadingItem size="large" width="small" />
            <LoadingItem size="standard" width="full" />
            <LoadingItem size="standard" width="full" />
          </Stack>
        )}
      </Stack>
    </>
  );
};
